/**
 * @fileoverview Component for changing a user's password.
 * This component includes a form for changing the password and displays a message if the password is incorrect or duplicated.
 */

import { AxiosError } from 'axios'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api'
import NovoicDashboardLogo from '../../assets/novoic-dashboard-logo.svg'
import { ChangePasswordForm } from './ChangePasswordForm'

/**
 * @function ChangePassword
 * @description Component for changing a user's password.
 * @returns {JSX.Element} - A React component that renders the change password form.
 */
export default function ChangePassword() {
    const navigate = useNavigate()

    const [isPending, setIsPending] = useState(false)
    const [isWrongPassword, setIsWrongPassword] = useState(false)
    const [isDuplicatedPassword, setIsDuplicatedPassword] = useState(false)

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsPending(true)

        const formData = new FormData(e.currentTarget)
        const payload: Record<string, string> = {}

        payload.password = formData.get('password') as string
        payload.new_password = formData.get('new_password') as string

        try {
            await api.post('/auth/password', payload)
            navigate('/app')
        } catch (e: unknown) {
            if ((e as AxiosError).response.status === 400) {
                setIsWrongPassword(true)
            } else if ((e as AxiosError).response.status === 409) {
                setIsDuplicatedPassword(true)
            } else {
                console.error(e)
            }
        } finally {
            setIsPending(false)
        }
    }

    return (
        <div className="nv-grid min-h-screen">
            <div className="lg-container max-h-full items-center content-center justify-center col-start-1 md:col-start-2 lg:col-start-4">
                <div className="w-full max-w-md space-y-8">
                    <div className="flex flex-col items-center gap-y-4">
                        <NovoicDashboardLogo className="w-56" />
                        <h2 className="text-center text-3xl font-bold font-serif text-gray-900">
                            Create new password
                        </h2>

                        <p className="text-gray-600 font-regular text-center">
                            For security reasons, you must update your password every 90 days.
                            Please create a unique new password that you have not previously used.
                        </p>
                    </div>

                    <ChangePasswordForm
                        onSubmit={onSubmit}
                        isWrongPassword={isWrongPassword}
                        isDuplicatedPassowrd={isDuplicatedPassword}
                        isPending={isPending}
                    />
                </div>
            </div>
        </div>
    )
}
